import { types, flow } from 'mobx-state-tree';
import axios from 'axios';

const FlagCount = types.model('FlagCount', {
  error: types.optional(types.number, 0),
  warning: types.optional(types.number, 0),
  info: types.optional(types.number, 0)
});

const BotSummary = types.model('BotSummary', {
  botId: types.string,
  recallBotId: types.maybeNull(types.string),
  scheduledStartTime: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  stage: types.maybeNull(types.string),
  assignedTo: types.maybeNull(types.string),
  companyId: types.maybeNull(types.string),
  meetingTitle: types.maybeNull(types.string),
  meetingUrl: types.maybeNull(types.string),
  flagCount: types.optional(FlagCount, {}),
  flags: types.optional(types.frozen(), [])
});

const ListState = types.model('ListState', {
  loading: types.optional(types.boolean, false),
  error: types.maybeNull(types.string),
  upcoming: types.array(BotSummary),
  recent: types.array(BotSummary),
  generatedAt: types.maybeNull(types.string)
});

const DetailState = types.model('DetailState', {
  loading: types.optional(types.boolean, false),
  error: types.maybeNull(types.string),
  result: types.maybeNull(types.frozen())
});

export const BotInspectorStore = types
  .model('BotInspectorStore', {
    list: types.optional(ListState, {}),
    detail: types.optional(DetailState, {})
  })
  .actions(self => ({
    loadList: flow(function* () {
      self.list.loading = true;
      self.list.error = null;
      try {
        const response = yield axios.get('/murphy/bot-inspector');
        self.list.upcoming = response.data.upcoming || [];
        self.list.recent = response.data.recent || [];
        self.list.generatedAt = response.data.generatedAt || null;
      } catch (error) {
        console.error('BotInspectorStore: list load failed', error);
        self.list.error = error.response?.data?.error || error.message;
        self.list.upcoming = [];
        self.list.recent = [];
      } finally {
        self.list.loading = false;
      }
    }),

    loadBot: flow(function* (botId) {
      self.detail.loading = true;
      self.detail.error = null;
      self.detail.result = null;
      try {
        const response = yield axios.get('/murphy/bot-inspector', { params: { botId } });
        self.detail.result = response.data;
      } catch (error) {
        console.error('BotInspectorStore: bot load failed', error);
        self.detail.error = error.response?.data?.error || error.message;
      } finally {
        self.detail.loading = false;
      }
    }),

    clearDetail() {
      self.detail.result = null;
      self.detail.error = null;
      self.detail.loading = false;
    },

    /**
     * Toggle the acknowledgement of one consistency-check warning on this bot.
     * Refreshes the detail view after the write so the UI reflects the new
     * acknowledged state.
     */
    acknowledgeFlag: flow(function* (botId, check, action /* 'acknowledge' | 'unacknowledge' */) {
      try {
        yield axios.get('/murphy/bot-inspector', { params: { botId, check, action } });
      } catch (error) {
        console.error('BotInspectorStore: acknowledgeFlag failed', error);
        self.detail.error = error.response?.data?.error || error.message;
        return;
      }
      // Refresh the detail so the dismissed/un-dismissed state shows immediately.
      yield self.loadBot(botId);
    })
  }));
